import { MAX_SAMPLE_ACCURACY_M } from '@odhaczka/core';
import { useState } from 'react';
import { geoReverse, geoSupported, requestGeo } from '../../lib/geo';
import { ChainInput } from './ChainInput';
import { inputCls, primaryBtn } from './classes';
import { Field } from './Field';

export interface StoreDraft {
  name: string;
  chainName: string;
  address: string;
  /** Pozycja z „Adres stąd” — tylko gdy adres nie został potem zmieniony ręcznie. */
  lat?: number | null;
  lng?: number | null;
  geoLabel?: string | null;
}

interface Props {
  initial?: Partial<StoreDraft>;
  withAddress?: boolean;
  submitLabel: string;
  autoFocus?: boolean;
  onSubmit: (draft: StoreDraft) => Promise<void>;
}

interface Here {
  lat: number | null;
  lng: number | null;
  label: string | null;
  /** Adres wpisany przez „Adres stąd” — ręczna zmiana odpina współrzędne. */
  address: string;
}

export function StoreForm({ initial, withAddress = false, submitLabel, autoFocus = false, onSubmit }: Props) {
  const [name, setName] = useState(initial?.name ?? '');
  const [chainName, setChainName] = useState(initial?.chainName ?? '');
  const [address, setAddress] = useState(initial?.address ?? '');
  const [busy, setBusy] = useState(false);
  const [here, setHere] = useState<Here | null>(null);
  const [locating, setLocating] = useState(false);
  const [hereError, setHereError] = useState<string | null>(null);
  const hereActive = here && here.address === address ? here : null;

  const submit = async (e: { preventDefault(): void; stopPropagation(): void }) => {
    e.preventDefault();
    // Formularz bywa renderowany w portalu wewnątrz innego formularza — nie wysyłamy rodzica.
    e.stopPropagation();
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      await onSubmit({ name, chainName, address, lat: hereActive?.lat ?? null, lng: hereActive?.lng ?? null, geoLabel: hereActive?.label ?? null });
    } finally {
      setBusy(false);
    }
  };

  // Gest użytkownika — tu wolno zapytać o zgodę na lokalizację.
  const fillHere = async () => {
    setLocating(true);
    setHereError(null);
    try {
      const res = await requestGeo();
      if (res.status === 'unsupported') return setHereError('Ta przeglądarka nie udostępnia lokalizacji.');
      if (res.status === 'denied') return setHereError('Brak zgody na lokalizację. Można ją włączyć w ustawieniach przeglądarki.');
      const pos = res.position;
      if (!pos) return setHereError('Nie udało się ustalić położenia. Spróbuj jeszcze raz za chwilę.');
      const place = await geoReverse(pos.lat, pos.lng);
      // Pozycja mniej dokładna niż próbki z wizyt nie staje się położeniem sklepu (adres i tak się wypełni).
      const precise = pos.accM <= MAX_SAMPLE_ACCURACY_M;
      const text = place?.address ?? address;
      setAddress(text);
      setHere({ lat: precise ? pos.lat : null, lng: precise ? pos.lng : null, label: place?.label ?? null, address: text });
      if (!place) setHereError(precise ? 'Położenie zapisane, ale bez internetu nie da się ustalić adresu.' : 'Nie udało się ustalić adresu.');
      else if (!precise) setHereError(`Położenie jest niedokładne (±${Math.round(pos.accM)} m) — sprawdź adres.`);
    } finally {
      setLocating(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <Field label="Nazwa">
        <input
          required
          autoFocus={autoFocus}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="np. Auchan Bielany"
          autoComplete="off"
          autoCapitalize="words"
          enterKeyHint="next"
          className={inputCls}
        />
      </Field>
      <Field label="Sieć">
        <ChainInput value={chainName} onChange={setChainName} />
      </Field>
      {withAddress && (
        <div className="flex flex-col gap-1">
          <Field label="Adres (opcjonalnie)">
            <input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="np. ul. Kasprowicza 145"
              autoComplete="off"
              enterKeyHint="done"
              className={inputCls}
            />
          </Field>
          {geoSupported() && (
            <div className="flex flex-wrap items-center gap-x-3 px-1">
              <button
                type="button"
                onClick={() => void fillHere()}
                disabled={locating}
                className="min-h-11 text-sm font-medium text-brand-700 disabled:opacity-60 dark:text-brand-500"
              >
                {locating ? 'Ustalam położenie…' : '📍 Adres stąd'}
              </button>
              {hereActive?.label && <span className="text-xs text-stone-500">© OpenStreetMap</span>}
            </div>
          )}
          {hereError && <p className="px-1 text-sm text-stone-600 dark:text-stone-400">{hereError}</p>}
        </div>
      )}
      <button disabled={busy || !name.trim()} className={primaryBtn}>
        {submitLabel}
      </button>
    </form>
  );
}
